import { MenuItem, Select, SelectChangeEvent, styled, Typography } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import {
  CaseResultDetails,
  EconsultSpecialist,
  EconsultUnavailabilityDate,
  EconsultUtils,
  NameUtils
} from '@oh-vcp/components-common';
import useProgramService from '../services/useProgramService';
import CaseService from '../services/caseService';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';

const containerWidth = EconsultUtils.isMobileDevice() ? '92%' : '100%';
const itemWidth = EconsultUtils.isMobileDevice() ? '89%' : '100%';
const SelectContainer = styled(Select)({
  height: 30,
  width: containerWidth,
  paddingTop: 5,
  backgroundColor: 'white',
  '& .MuiSelect-icon': {
    backgroundColor: 'white',
    right: '0px'
  },
  '& .MuiSelect-select': {
    padding: 0,
    paddingLeft: 10
  }
});

interface EconsultProgramSpecialistSelectorProps {
  caseResult: CaseResultDetails;
  selectedSpecialist: string | undefined;
  onSpecialistSelected: (
    specialist: EconsultSpecialist | undefined,
    outOfOfficeInAWeek?: EconsultUnavailabilityDate | null
  ) => void;
}

const EconsultProgramSpecialistSelector: FC<EconsultProgramSpecialistSelectorProps> = ({
  caseResult,
  selectedSpecialist,
  onSpecialistSelected
}) => {
  const [myHeight, setMyHeight] = useState<number>(28);
  const { programId, consultantId, referrerLhin, patientAge } = caseResult || {};
  const specialists = useRef<EconsultSpecialist[]>();
  const { caseAssignChanged } = useEconsultCaseDetailsContext();

  const { data: initSpecialists } = useProgramService(programId?.toString());

  const fetchSpecialists = async () => {
    specialists.current = await CaseService.getProgramSpecialists(programId?.toString());
  };

  useEffect(() => {
    if (initSpecialists) {
      specialists.current = initSpecialists;
    }
  }, [initSpecialists]);

  useEffect(() => {
    console.log(`case assign changed: ${caseAssignChanged}, going to update specialists`);
    fetchSpecialists();
  }, [caseAssignChanged]);

  const getSpecialistOutOfOffice = (specialist: EconsultSpecialist) => {
    if (!specialist.unavailabilities) {
      return null;
    }
    const currentOOODate = EconsultUtils.getCurrentOutOfOfficeDate(specialist.unavailabilities);
    return currentOOODate;
  };

  const isAgeGroupRestricted = (specialist: EconsultSpecialist) => {
    if (specialist.acceptedAge === 'ADULT') {
      return patientAge < 19;
    }
    return patientAge >= 19;
  };

  const isSpecialistOptionDisabled = (specialist: EconsultSpecialist) => {
    if (specialist.isEconsultDecomissioned || getSpecialistOutOfOffice(specialist) !== null) {
      return true;
    }
    if (specialist.acceptedAge && isAgeGroupRestricted(specialist)) {
      return true;
    }
    if (consultantId === specialist.userId) {
      return false;
    }
    return (
      specialist.acceptedLhins && referrerLhin && specialist.acceptedLhins.indexOf(referrerLhin) < 0
    );
  };

  const getSpecialistOutOfOfficeInAWeek = (specialist: EconsultSpecialist) => {
    if (!specialist.unavailabilities) {
      return null;
    }
    const oooDate = EconsultUtils.getOutOfOfficeDateInAWeek(specialist.unavailabilities);
    return oooDate;
  };

  const getSpecialistOptionName = (specialist: EconsultSpecialist) => {
    let optionName = NameUtils.makeName(
      specialist.lastName,
      specialist.firstName,
      specialist.salutation,
      null,
      specialist.middleName,
      null
    );

    if (!specialist.numOfInProgress) {
      specialist.numOfInProgress = 0;
    }
    if (!specialist.numOfAssigned) {
      specialist.numOfAssigned = 0;
    }
    let disable = false;
    if (specialist.isEconsultDecomissioned) {
      optionName += ' - deregistered';
      disable = true;
    } else {
      const currentOooDate = getSpecialistOutOfOffice(specialist);
      if (currentOooDate !== null) {
        optionName += ` - unavailable from ${currentOooDate.startDate} to ${currentOooDate.endDate}`;
        disable = true;
      } else {
        const nextWeekOooDate = getSpecialistOutOfOfficeInAWeek(specialist);
        if (nextWeekOooDate !== null) {
          optionName += ` - unavailable from ${nextWeekOooDate.startDate} to ${nextWeekOooDate.endDate}`;
        }
      }
    }
    if (!disable) {
      if (specialist.acceptedAge && isAgeGroupRestricted(specialist)) {
        optionName += ' - restricted patient age group';
        disable = true;
      } else if (specialist.acceptedLhins && referrerLhin) {
        if (specialist.acceptedLhins.indexOf(referrerLhin) < 0) {
          optionName += ' - restricted Regions';
          disable = true;
        }
      }
    }
    if (!disable) {
      if (specialist.maximumCases7Days) {
        optionName += ` - ${specialist.receivedCases7Days}/${specialist.maximumCases7Days} cases in the last 7 days, `;
      } else {
        optionName += ' - ';
      }
      optionName += `${specialist.numOfAssigned} case${
        specialist.numOfAssigned === 1 ? '' : 's'
      } in the last 30 days, ${specialist.numOfInProgress} pending response`;
    }
    return optionName;
  };

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleSpecialistSelected = (e: SelectChangeEvent<unknown>) => {
    const s = specialists.current?.find(
      (spec) => spec.userId === parseInt(e.target.value as string, 10)
    );
    if (s) {
      const oooDate = EconsultUtils.getOutOfOfficeDateInAWeek(s.unavailabilities);
      onSpecialistSelected(s, oooDate);
    } else {
      onSpecialistSelected(undefined);
    }
    setMyHeight(28);
    if (
      EconsultUtils.isMobileDevice() &&
      s &&
      !EconsultUtils.getOutOfOfficeDateInAWeek(s.unavailabilities)
    ) {
      const consultantSelected = getSpecialistOptionName(s);
      if (consultantSelected && consultantSelected.length > 105) {
        setMyHeight(98);
      } else if (consultantSelected && consultantSelected.length > 77) {
        setMyHeight(82);
      } else if (consultantSelected && consultantSelected.length > 56) {
        setMyHeight(68);
      } else if (consultantSelected && consultantSelected.length > 37) {
        setMyHeight(54);
      }
    }
  };
  if (!specialists.current || specialists.current.length === 0) {
    fetchSpecialists();
  }
  if (!specialists.current || specialists.current.length === 0) return null;
  return (
    <SelectContainer
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left'
        }
      }}
      onClick={handleOnClick}
      value={
        selectedSpecialist &&
        specialists.current.some((s) => s.userId.toString() === selectedSpecialist)
          ? selectedSpecialist
          : '0'
      }
      onChange={handleSpecialistSelected}
      style={{ height: myHeight === 28 ? 'inherit' : myHeight }}>
      <MenuItem
        value="0"
        sx={{
          '&:hover': { color: 'initial' }
        }}>
        <Typography variant="body2">Select a specialist</Typography>
      </MenuItem>
      {specialists.current
        .sort((a, b) => {
          if (a.lastName > b.lastName) return 1;
          return -1;
        })
        .map((s) => (
          <MenuItem
            value={s.userId}
            disabled={isSpecialistOptionDisabled(s)}
            sx={{
              color: isSpecialistOptionDisabled(s) ? 'base.grey4' : 'inherit',
              opacity: '1 !important',
              '&.Mui-selected:hover': { color: 'black' }
            }}>
            {EconsultUtils.isMobileDevice() ? (
              <Typography
                variant="body2"
                noWrap={false}
                style={{ wordBreak: 'break-word', whiteSpace: 'normal', width: itemWidth }}>
                {getSpecialistOptionName(s)}
              </Typography>
            ) : (
              <Typography variant="body2">{getSpecialistOptionName(s)}</Typography>
            )}
          </MenuItem>
        ))}
    </SelectContainer>
  );
};

export default EconsultProgramSpecialistSelector;
